<script setup>
import { ref, watch, computed, getCurrentInstance, nextTick, onBeforeMount } from "vue";
import { useRoute } from "vue-router";
import { useCarteleraStore } from "@/store/carteleraStore";
import { storeToRefs } from "pinia";
import { getFormattedDate } from "@/utils/helpers";
import { verLote } from "@/utils/subastaRedirections";
import SpinnerLoader from "@/components/Spinner/SpinnerLoader.vue";

const route = useRoute();
const cartelera = useCarteleraStore();
const { lotes, categoriasLotes } = storeToRefs(cartelera);

const { appContext } = getCurrentInstance();
const storage_path = appContext.config.globalProperties.storage_path;

const categoriaId = computed(() => Number(route.params.id));
const categoriaActual = computed(() =>
  !categoriaId.value || !Array.isArray(categoriasLotes.value)
    ? null
    : categoriasLotes.value.find(t => Number(t.id) === categoriaId.value) || null
);

const mostrarFiltros = ref(true);
const filtros = ref({ tipificados: [], ubicaciones: [] });

const apiFilters = computed(() => {
  const tipIds = filtros.value.tipificados.map(t => t.id);
  const ubiIds = filtros.value.ubicaciones.map(u => u.id);

  return {
    per_page: 9,
    page: lotes.value?.current_page ?? 1,
    ...(tipIds.length
      ? { tipificado_id: tipIds }
      : { "tipificado:categoria_id": categoriaId.value }),
    ...(ubiIds.length ? { subasta_ubicacion_id: ubiIds } : {}),
  };
});

const fetchedOnce = ref(false);
const refetch = async (page = 1) => {
  await cartelera.updatePaginationLotes("current_page", page, apiFilters.value);
};

onBeforeMount(async () => {
  if (!categoriasLotes.value.length) {
    await cartelera.obtenerCategoriasLotes();
  }
  await refetch(1);
  fetchedOnce.value = true;
});

watch(
  () => categoriaId.value,
  async () => {
    filtros.value = { tipificados: [], ubicaciones: [] };
    await refetch(1);
  }
);

const opcionesTipificados = computed(() => {
  const catTips = categoriaActual.value?.tipificados;
  if (Array.isArray(catTips) && catTips.length) {
    return catTips.map(t => ({ id: t.id, nombre: t.nombre }));
  }
  const seen = new Map();
  (Array.isArray(lotes.value?.data) ? lotes.value.data : []).forEach(l => {
    const id = l?.tipificado?.id ?? l?.tipificado_id;
    const nombre = l?.tipificado?.nombre ?? l?.tipificado_nombre ?? "Tipificado";
    if (id != null && !seen.has(id)) seen.set(id, { id, nombre });
  });
  return [...seen.values()];
});

const opcionesUbicaciones = computed(() => {
  const arr = Array.isArray(lotes.value?.data) ? lotes.value.data : [];
  const idToName = new Map();

  const add = (id, u) => {
    if (!id) return;
    let nombre =
      u?.nombre ||
      (u?.direccion ? `${u.direccion} ${u.ciudad ?? ""}`.trim()
        : u?.descripcion ? `${u.descripcion}${u?.ciudad ? " - " + u.ciudad : ""}` : null);
    if (!nombre) nombre = `Ubicación ${id}`;
    if (!idToName.has(id)) idToName.set(id, nombre);
  };

  for (const l of arr) {
    const listas = [l?.subasta?.ubicaciones, l?.subasta_ubicaciones, l?.ubicaciones]
      .filter(Array.isArray);
    listas.flat().forEach(u => add(u?.id ?? u?.subasta_ubicacion_id ?? u, u));

    if (l?.subasta_ubicacion) add(l.subasta_ubicacion.id ?? l.subasta_ubicacion_id, l.subasta_ubicacion);
    if (l?.ubicacion) add(l.ubicacion.id ?? l.ubicacion_id, l.ubicacion);
    const idSolo = l?.subasta_ubicacion_id ?? l?.ubicacion_id;
    if (idSolo) add(idSolo, null);
  }

  return [...idToName.entries()].map(([id, nombre]) => ({ id, nombre }));
});

const toggleFiltro = async (tipo, valor) => {
  const list = filtros.value[tipo];
  const i = list.findIndex(f => f.id === valor.id);
  i >= 0 ? list.splice(i, 1) : list.push(valor);
  await nextTick();
  await refetch(1);
};

const goToPage = async (page) => {
  const last = lotes.value?.last_page ?? 1;
  if (page > 0 && page <= last) {
    await refetch(page);
    document.getElementById("carteleraLote")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const hasData = computed(() => Array.isArray(lotes.value?.data) && lotes.value.data.length > 0);
</script>


<template>
  <div v-if="cartelera.cargando" class="spinner-overlay">
    <SpinnerLoader size="64px" />
  </div>

  <div
    v-if="categoriaActual?.archivo"
    class="position-relative overflow-hidden mt-6 mt-lg-0"
  >
    <img
      class="banner-img"
      :src="`${storage_path}/${categoriaActual.archivo.hash}.${categoriaActual.archivo.extension}`"
      alt="Banner categoría"
    />
    <div
      class="position-absolute top-0 start-0 w-100 h-100"
      style="background-color: black; opacity: 0.35; z-index: 1"
    ></div>
    <div
      class="position-absolute top-50 start-50 translate-middle text-center text-white z-2 w-100 px-3"
      style="z-index: 2"
    >
      <h1 class="fw-bold display-5 font-roboto-slab text-white">
        Categoría {{ categoriaActual.nombre }}
      </h1>
    </div>
  </div>

  <section class="container">
    <div class="row mt-md-3 mt-0">
      <aside class="col-12 col-lg-3 mb-3">
        <div class="card shadow-none rounded-lg">
          <div
            class="card-header d-flex flex-wrap align-items-center gap-2 pb-3"
          >
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <p class="mb-0 fs-5 d-flex align-items-center fw-semiblod gap-2">
                <img src="@/assets/img/icons/layer.svg" alt="" />Filtros
              </p>
              <i
                class="cursor-pointer d-md-none"
                :class="
                  mostrarFiltros ? 'fa fa-chevron-up' : 'fa fa-chevron-down'
                "
                @click="mostrarFiltros = !mostrarFiltros"
              ></i>
            </div>

            <div class="w-100 pt-2">
              <div class="d-flex flex-wrap gap-2">
                <template v-for="(valores, tipo) in filtros" :key="tipo">
                  <template
                    v-for="valor in valores"
                    :key="`${tipo}-${valor.id}`"
                  >
                    <span
                      class="badge bg-badge-secondary text-white d-flex align-items-center gap-2 px-2 py-2 cursor-pointer fs-7"
                      style="white-space: normal; word-break: break-word"
                      @click="toggleFiltro(tipo, valor)"
                    >
                      <span>{{ valor.nombre }}</span>
                      <i class="fa fa-times cursor-pointer"></i>
                    </span>
                  </template>
                </template>
              </div>
            </div>
          </div>

          <div class="card-body pt-0" v-if="mostrarFiltros">
            <p class="mb-2 fs-5">Tipificados</p>
            <ul class="list-unstyled fw-bold">
              <li
                v-for="tip in opcionesTipificados"
                :key="`tip-${tip.id}`"
                class="mb-2"
              >
                <a
                  href="javascript:;"
                  class="filter-link text-subtitulos-secondary fs-7"
                  @click="toggleFiltro('tipificados', tip)"
                >
                  {{ tip.nombre }}
                </a>
              </li>
            </ul>

            <p class="mt-3 mb-2 fs-5">Ubicación</p>
            <ul class="list-unstyled fw-bold">
              <li
                v-for="ub in opcionesUbicaciones"
                :key="`ub-${ub.id}`"
                class="mb-2"
              >
                <a
                  href="javascript:;"
                  class="filter-link text-subtitulos-secondary fs-7"
                  @click="toggleFiltro('ubicaciones', ub)"
                >
                  {{ ub.nombre }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </aside>

      <div class="col-12 col-lg-9">
        <div
          class="col-12"
          v-if="fetchedOnce && !cartelera.cargando && !hasData"
        >
          <div class="text-center py-5 p-0">
            <img
              src="@/assets/img/illustrations/sin_resultados.png"
              alt="Sin lotes"
              style="max-width: 280px"
              class="mb-4"
            />
            <h4 class="text-muted">No hay lotes disponibles.</h4>
            <p class="text-muted fs-6">
              No hay lotes disponibles en este momento. <br />Te avisaremos
              cuando haya novedades.
            </p>
          </div>
        </div>

        <div class="row g-4" id="carteleraLote" v-if="hasData">
          <a
            class="col-sm-6 col-lg-4"
            v-for="(lote, index) in lotes.data"
            :key="index"
            href="javascript:;"
          >
            <div class="card shadow-none hover-zoom card-lote h-100">
              <div class="image-container">
                <div
                  v-if="lote.multimedia?.length > 0"
                  :id="`carousel-lotes-list-${lote.id}`"
                  class="carousel slide"
                  data-bs-ride="carousel"
                >
                  <div class="carousel-inner">
                    <div
                      class="carousel-item"
                      v-for="(multimedia, multimediaIndex) in lote.multimedia"
                      :key="multimedia.id ?? multimediaIndex"
                      :class="{ active: multimediaIndex === 0 }"
                    >
                      <div class="lote-imagen w-100">
                        <img
                          v-if="multimedia?.tipo === 'imagen'"
                          :src="`${storage_path}/${multimedia.archivo?.hash}.${multimedia.archivo?.extension}`"
                          class="card-img-top"
                          alt="Multimedia imagen"
                        />
                        <video
                          v-else-if="multimedia?.tipo === 'video'"
                          muted
                          playsinline
                          controls
                          :src="`${storage_path}/${multimedia.archivo?.hash}.${multimedia.archivo?.extension}`"
                          class="card-img-top"
                        ></video>
                        <img
                          v-else
                          src="@/assets/img/logos/lote-sin-foto.png"
                          class="card-img-top-default"
                          alt="Sin multimedia"
                          style="width: 121px; height: 93px"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    class="carousel-control-prev"
                    type="button"
                    :data-bs-target="`#carousel-lotes-list-${lote.id}`"
                    data-bs-slide="prev"
                  >
                    <span
                      class="carousel-control-prev-icon"
                      aria-hidden="true"
                    ></span>
                    <span class="visually-hidden">Anterior</span>
                  </button>
                  <button
                    class="carousel-control-next"
                    type="button"
                    :data-bs-target="`#carousel-lotes-list-${lote.id}`"
                    data-bs-slide="next"
                  >
                    <span
                      class="carousel-control-next-icon"
                      aria-hidden="true"
                    ></span>
                    <span class="visually-hidden">Siguiente</span>
                  </button>
                </div>

                <div v-else class="no-image-placeholder">
                  <i class="fa fa-image"></i>
                  <p>Sin imagen disponible</p>
                </div>
              </div>

              <div class="card-body">
                <p
                  class="fs-6 mb-2 text-truncate"
                  style="
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  "
                >
                  {{ lote.titulo }}
                </p>

                <p class="text-subtitulos-secondary mb-2 fs-13">
                  <i class="fa fa-tag me-1"></i> LOTE {{ lote.codintlote }}
                </p>

                <p class="text-subtitulos-secondary mb-2 fs-13">
                  <i class="fa-solid fa-calendar-day fs-6 me-1"></i>
                  {{ getFormattedDate(lote.subasta?.fecha_hora_inicio) }}
                </p>

                <p class="text-subtitulos-secondary mb-2 fs-13">
                  <i class="fa-regular fa-folder fs-6 me-1"></i> (ID
                  {{ lote.subasta?.external_id }})
                </p>

                <button
                  class="btn btn-outline-success w-100 mt-2"
                  @click="verLote(lote.id)"
                >
                  Ver Lote
                </button>
              </div>
            </div>
          </a>

          <div
            class="d-flex justify-content-center"
            v-if="hasData && !cartelera.cargando && lotes?.last_page > 1"
          >
            <div
              class="d-flex justify-content-between align-items-center mt-3 px-3"
            >
              <nav class="mt-3">
                <div class="custom-pagination w-100">
                  <a
                    href="#"
                    @click.prevent="goToPage(lotes.current_page - 1)"
                    class="page-nav"
                    :class="{ disabled: lotes.current_page === 1 }"
                  >
                    <i class="fa fa-chevron-left"></i> Anterior
                  </a>

                  <a
                    v-for="page in lotes.last_page"
                    :key="page"
                    href="#"
                    class="page-number fw-bold text-white"
                    :class="{ active: page === lotes.current_page }"
                    @click.prevent="goToPage(page)"
                  >
                    {{ page }}
                  </a>

                  <a
                    href="#"
                    @click.prevent="goToPage(lotes.current_page + 1)"
                    class="page-nav"
                    :class="{
                      disabled: lotes.current_page === lotes.last_page,
                    }"
                  >
                    Siguiente <i class="fa fa-chevron-right"></i>
                  </a>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.banner-img {
  width: 100%;
  height: 340px;
  object-fit: cover;
  object-position: center;
}

.image-container {
  background: #f8f9fa;
  width: 100%;
  overflow: hidden;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  aspect-ratio: 4 / 3;
  position: relative;
}
.image-container .carousel,
.image-container .carousel-inner,
.image-container .carousel-item {
  height: 100%;
}
.image-container .carousel-item img,
.image-container .carousel-item video,
.image-container .carousel-item iframe {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}
.image-container .carousel-item img {
  transform: scale(1.08);
  transform-origin: center;
}
.image-container .carousel-control-prev,
.image-container .carousel-control-next {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition:
    opacity 0.2s,
    visibility 0.2s;
  z-index: 3;
}
.card:hover .image-container .carousel-control-prev,
.card:hover .image-container .carousel-control-next {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}
@media (hover: none) {
  .image-container .carousel-control-prev,
  .image-container .carousel-control-next {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6c757d;
}
.no-image-placeholder i {
  font-size: 3rem;
}
.no-image-placeholder p {
  margin-top: 0.5rem;
  font-size: 0.9rem;
}
</style>
